/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
/** Klaviyo public site key (company_id). Public by design; it is in every page's Klaviyo script URL. */
const KLAVIYO_SITE_ID = 'VZV2Ax';

class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (this.dataset.klaviyoList && this.form) this.form.addEventListener('submit', this.#subscribeToKlaviyo);

    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }
  }

  /**
   * TGC: send the sign-up to the Klaviyo list set on the block (the popup's list,
   * so the Welcome flow and its code fire) instead of creating a Shopify customer.
   * Public client endpoint, public site key only.
   * @param {SubmitEvent} event
   */
  #subscribeToKlaviyo = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="email"]'));
    const button = form.querySelector('button');
    if (!input || !input.value) return;
    const email = input.value.trim();
    if (button) button.disabled = true;

    let ok = false;
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_SITE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Footer signup',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.klaviyoList } } },
          },
        }),
      });
      ok = response.ok;
    } catch {
      ok = false;
    }

    if (ok) {
      // Tell Klaviyo's onsite script who this is, so the popup stops asking.
      const klaviyo = /** @type {any} */ (window).klaviyo;
      if (klaviyo && typeof klaviyo.identify === 'function') klaviyo.identify({ email });
      input.value = '';
    } else if (button) {
      button.disabled = false;
    }
    this.#showMessage(form, ok, ok ? this.dataset.klaviyoSuccess || '' : 'Something went wrong. Please try again.');
  };

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} ok
   * @param {string} text
   */
  #showMessage(form, ok, text) {
    form.querySelector('.email-signup__message')?.remove();
    const message = document.createElement('div');
    message.className = `email-signup__message email-signup__message--${ok ? 'success' : 'error'}`;
    message.setAttribute('role', 'status');
    const p = document.createElement('p');
    p.className = 'email-signup__message-text';
    p.textContent = text;
    message.append(p);
    form.append(message);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
